<template>
  <div
    class="tw-rounded-sm tw-bg-white tw-pt-4"
    :data-test-id="`${tier}-phishing-simulation-lures-chart`"
  >
    <card-placeholder-banner
      type="PHISHING_SIMULATION"
      :data-test-id="`${tier}-phishing-simulation-lures-banner`"
      :data-test-id-base="`${tier}-phishing-simulation-lures`"
    >
      <template #title>{{ $t("lures-sent-vs-clicked-title") }}</template>
      <template #text>
        {{ $t("dashboard-phishing-simulation") }}
      </template>
    </card-placeholder-banner>
    <div class="tw-relative tw-h-72 tw-w-full tw-p-4">
      <canvas
        id="lures-chart-placeholder"
        :data-test-id-base="`${tier}-phishing-simulation-lures-chart-canvas`"
        width="100"
        height="auto"
        role="image"
      ></canvas>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import CardPlaceholderBanner from "../CardPlaceholderBanner/CardPlaceholderBanner.vue";

import Chart from "chart.js/auto";

import { useIsMspRoute } from "@/composables/useIsMspRoute";

const { isMspRoute } = useIsMspRoute();

const tier = isMspRoute.value ? "msp" : "customer";

const style = getComputedStyle(document.body);
const textColor = computed(() => style.getPropertyValue("--chartWhite"));

const blue = {
  default: "rgba(14, 156, 255, 1)",
  half: "rgba(14, 156, 255, 0.5)",
  quarter: "rgba(14, 156, 255, 0.25)",
  zero: "rgba(14, 156, 255, 0)",
};

const red = {
  default: "rgba(210, 63, 63, 1)",
  half: "rgba(210, 63, 63, 0.85)",
  quarter: "rgba(210, 63, 63, 0.5)",
  zero: "rgba(210, 63, 63, 0.25)",
};

const { t } = useI18n();

const luresSentMock = [0, 0, 0, 0, 8, 60];

const luresClickedMock = [0, 0, 0, 0, 0, 8];

const labels = [
  "2024-04-01",
  "2024-05-01",
  "2024-06-01",
  "2024-07-01",
  "2024-08-01",
  "2024-09-01",
].map((item: string) => {
  const date = new Date(item);
  const formatter = new Intl.DateTimeFormat("en", {
    month: "short",
    year: "numeric",
  });

  return formatter.format(date);
});

const initChart = () => {
  const ctx = (
    document.getElementById("lures-chart-placeholder") as HTMLCanvasElement
  ).getContext("2d");

  if (!ctx) {
    return;
  }

  const gradientBlue = ctx.createLinearGradient(0, 0, 0, 250);
  gradientBlue.addColorStop(0, blue.half);
  gradientBlue.addColorStop(0.35, blue.quarter);
  gradientBlue.addColorStop(1, blue.zero);

  const gradientRed = ctx.createLinearGradient(0, 0, 0, 250);
  gradientRed.addColorStop(0, red.half);
  gradientRed.addColorStop(0.35, red.quarter);
  gradientRed.addColorStop(1, red.zero);

  new Chart(ctx, {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: t("lures-sent-title"),
          data: luresSentMock,
          backgroundColor: gradientBlue,
          pointBackgroundColor: blue.default,
          borderColor: blue.default,
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.4,
          fill: true,
          type: "line",
          yAxisID: "y",
        },
        {
          label: t("lures-clicked-title"),
          data: luresClickedMock,
          backgroundColor: gradientRed,
          pointBackgroundColor: red.default,
          borderColor: red.default,
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.4,
          fill: true,
          type: "line",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      scales: {
        x: {
          grid: {
            display: false,
            drawTicks: false,
          },
          border: {
            display: false,
          },
          ticks: {
            padding: 12,
            color: textColor.value,
          },
          offset: false,
        },
        y: {
          beginAtZero: true,
          position: "left",
          title: {
            display: true,
            text: t("lures-sent-title"),
            color: blue.default,
            font: {
              size: 18,
            },
          },
          border: {
            display: false,
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            padding: 12,
            precision: 0,
            color: textColor.value,
          },
        },
        y1: {
          position: "right",
          title: {
            display: true,
            text: t("lures-clicked-title"),
            color: red.default,
            font: {
              size: 18,
            },
          },
          border: {
            display: false,
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            display: false,
            padding: 12,
            precision: 0,
            color: textColor.value,
          },
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            padding: 24,
            color: textColor.value,
          },
        },
      },
    },
  });
};

onMounted(() => initChart());
</script>
